/**
 * Attaches an HLS clip manifest (video-transcoder's R2/CDN pipeline) to a
 * `<video>` element, falling back to a caller-supplied legacy MP4 `src` on
 * any failure -- older videos transcoded before the HLS pipeline existed
 * have no package, and only chat payloads say so up front (`hls_ready`, which
 * their callers check before attaching), so elsewhere "try HLS, fall back" is
 * the only signal available.
 */

import { getCookie } from "~/modules/shared/utils/auth";

export type HlsClipSource = {
	videoId: string;
	start: number;
	end: number;
};

export type HlsSourceHandle = {
	destroy: () => void;
};

/** An `HlsClipSource` spanning a whole video, or undefined when it can't be played as HLS. */
export const fullVideoHlsSource = (
	videoId: string | null | undefined,
	durationSeconds: number | null | undefined,
): HlsClipSource | undefined =>
	videoId && durationSeconds && durationSeconds > 0
		? { videoId, start: 0, end: durationSeconds }
		: undefined;

let hlsModulePromise: Promise<typeof import("hls.js")> | undefined;
const loadHlsJs = () => {
	hlsModulePromise ??= import("hls.js");
	return hlsModulePromise;
};

// The whole session has to fit one browser cookie (~100 ids), and the
// server merges these with ids other tabs already hold -- so a page asks for
// well under that, leaving them room.
const MAX_SESSION_IDS = 60;
// Comfortably inside the server's default token lifetime (1h).
const SESSION_REFRESH_MS = 45 * 60 * 1000;
// A failed mint never covers anything, so without this every later attach
// -- each hover in a grid -- would POST again into the same failure.
const MINT_FAILURE_BACKOFF_MS = 60 * 1000;

const requestedIds = new Set<string>();
let mintedIds = new Set<string>();
let mintedAt = 0;
let mintFailedAt = Number.NEGATIVE_INFINITY;
let pendingMint: Promise<ReadonlySet<string>> | undefined;
let attachedPlayers = 0;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;

/** Resolves to the ids that mint covered (attempted), whether or not it succeeded. */
const scheduleMint = (): Promise<ReadonlySet<string>> => {
	let snapshot: ReadonlySet<string> = new Set();
	// Deferred a macrotask so every player mounting in the same render (a
	// carousel's slides, a grid) joins one POST instead of racing N of them.
	return new Promise<void>((resolve) => setTimeout(resolve, 0))
		.then(async () => {
			const ids = [...requestedIds].slice(-MAX_SESSION_IDS);
			snapshot = new Set(ids);
			const response = await fetch("/api/videos/hls-session", {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-XSRF-TOKEN": getCookie("XSRF-TOKEN") ?? "",
				},
				body: JSON.stringify({ video_ids: ids }),
			});
			if (!response.ok) {
				throw new Error(`hls-session mint failed: ${response.status}`);
			}
			mintedIds = new Set(ids);
			mintedAt = Date.now();
			scheduleRefresh(SESSION_REFRESH_MS);
		})
		.catch(() => {
			mintFailedAt = Date.now();
			// Otherwise one failed refresh (a 429, a deploy) would end refreshes
			// for good, and every playing video would outlive its token. Only
			// for a session that did mint: where minting always fails (e.g.
			// presigned mode without a signing key) this would just poll.
			if (mintedAt > 0) {
				scheduleRefresh(MINT_FAILURE_BACKOFF_MS);
			}
		})
		.then(() => snapshot)
		.finally(() => {
			pendingMint = undefined;
		});
};

// A player already streaming never re-attaches, so without this a long or
// paused video would outlive its token and start failing segments.
const scheduleRefresh = (delayMs: number) => {
	clearTimeout(refreshTimer);
	refreshTimer = setTimeout(() => {
		if (attachedPlayers > 0) {
			pendingMint ??= scheduleMint();
		}
	}, delayMs);
};

const inSessionWindow = (videoId: string) =>
	[...requestedIds].slice(-MAX_SESSION_IDS).includes(videoId);

/**
 * Makes sure the `hls_session` cookie authorizes `videoId` at the CDN
 * Worker. HlsSessionService merges each mint into the cookie's existing,
 * still-authorized ids, so sending this page's requested ids is enough;
 * sending all of them (not just the new one) also restores the session
 * after the cookie expires or is cleared. Irrelevant under
 * `HLS_DELIVERY_MODE=presigned`, but the frontend can't see the mode, and a
 * failed mint just means the player falls back to MP4.
 */
export const ensureHlsSession = async (videoId: string): Promise<void> => {
	// Re-inserting moves the id to the end, so the cap drops the least recent.
	requestedIds.delete(videoId);
	requestedIds.add(videoId);
	// Twice at most: a second round is only worth it when the id arrived
	// after an in-flight POST had snapshotted its ids -- not when newer ids
	// have already pushed it out of the window, which a retry can't change.
	for (let attempt = 0; attempt < 2; attempt++) {
		if (mintedIds.has(videoId) && Date.now() - mintedAt < SESSION_REFRESH_MS) {
			return;
		}
		if (Date.now() - mintFailedAt < MINT_FAILURE_BACKOFF_MS) {
			return;
		}
		pendingMint ??= scheduleMint();
		if ((await pendingMint).has(videoId) || !inSessionWindow(videoId)) {
			return;
		}
	}
};

/**
 * Authorizes a whole set of videos up front in one POST -- for hover grids,
 * so each hover doesn't have to mint (and wait on) a session of its own.
 */
export const prewarmHlsSession = (videoIds: Iterable<string>): void => {
	for (const videoId of videoIds) {
		void ensureHlsSession(videoId);
	}
};

export const attachHlsClip = (
	video: HTMLVideoElement,
	source: HlsClipSource,
	onFallback: () => void,
	// Where playback will begin, in the manifest's timeline. Without it hls.js
	// loads from the playlist start, so a clip deep into a whole-video
	// manifest would first download that video's opening segments.
	startPosition = 0,
): HlsSourceHandle => {
	let destroyed = false;
	let hls: import("hls.js").default | undefined;
	attachedPlayers++;

	const manifestUrl = `/api/videos/${encodeURIComponent(source.videoId)}/hls-clip?start=${source.start}&end=${source.end}`;

	const fallback = () => {
		if (destroyed) {
			return;
		}
		// hls.js must release the element before the caller reassigns `src`
		// directly -- otherwise a live MediaSource stays attached underneath
		// the fallback and can reassert itself against the plain src we're
		// about to set.
		hls?.destroy();
		hls = undefined;
		onFallback();
	};

	let onNativeError: (() => void) | undefined;

	// Segments come from the CDN Worker, which rejects any video the cookie
	// doesn't cover -- so the session must be in place before the first one
	// is requested, not raced against it.
	void ensureHlsSession(source.videoId).then(async () => {
		if (destroyed) {
			return;
		}
		// hls.js wherever MSE exists, native HLS only where it doesn't (older
		// iOS) -- not the other way round: Chrome now claims native HLS support
		// too, and its implementation errored out on these packages, falling
		// every Chrome player back to MP4.
		const Hls = await loadHlsJs().then(
			(module) => module.default,
			() => undefined,
		);
		if (destroyed) {
			return;
		}
		if (Hls?.isSupported()) {
			hls = new Hls({
				startPosition,
				// Segments are cross-origin (cdn.<apex>), so the hls_session
				// cookie only goes with them on a credentialed request. Presigned
				// URLs authorize themselves, and a bucket's CORS answering "*"
				// would refuse a credentialed one.
				xhrSetup: (xhr, url) => {
					xhr.withCredentials = !url.includes("X-Amz-Signature=");
				},
			});
			hls.on(Hls.Events.ERROR, (_event, data) => {
				// hls.js only flags a load error fatal after exhausting its
				// retries (tens of seconds of backoff). A 401/403/404 won't change
				// on retry -- an unauthorized session or a package missing from
				// the store -- so give up on the first one.
				const code = data.response?.code;
				if (data.fatal || code === 401 || code === 403 || code === 404) {
					fallback();
				}
			});
			hls.loadSource(manifestUrl);
			hls.attachMedia(video);
			return;
		}
		if (video.canPlayType("application/vnd.apple.mpegurl")) {
			onNativeError = () => {
				video.removeEventListener("error", onNativeError as () => void);
				fallback();
			};
			video.addEventListener("error", onNativeError, { once: true });
			video.src = manifestUrl;
			return;
		}
		fallback();
	});

	return {
		destroy: () => {
			if (!destroyed) {
				attachedPlayers--;
			}
			destroyed = true;
			if (onNativeError) {
				video.removeEventListener("error", onNativeError);
			}
			hls?.destroy();
			hls = undefined;
		},
	};
};
